/**
 * HiBFF — Stripe Checkout success page.
 *
 * Lands here after a successful (or cancelled) Stripe Checkout redirect.
 * URL shape:  /billing/success?session_id=cs_test_...
 *
 * Per the Stripe playbook, we MUST poll the backend's status endpoint until
 * payment_status flips to "paid". The webhook is the backup path; polling
 * is the primary UX update.
 */
import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { apiClient } from '../api/client';
import HeartsReceiptCelebration from '../components/billing/HeartsReceiptCelebration';

const MAX_POLLS = 8;
const POLL_INTERVAL_MS = 2000;

const BillingSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');
  const [state, setState] = useState({ phase: 'checking', message: 'Confirming your payment with Stripe…' });
  const pollCount = useRef(0);
  const finished = useRef(false);

  useEffect(() => {
    // Dev/QA preview — render the celebration with deterministic mock data
    // without hitting Stripe. Trigger via /billing/success?preview=hearts.
    if (searchParams.get('preview') === 'hearts') {
      finished.current = true;
      setState({
        phase: 'success',
        purpose: 'hearts',
        heartsCredited: 200,
        newBalance: 100200,
        parentEmailMasked: 'r***@hibff.com',
        statementDescriptor: 'HIBFF TEEN COACHING',
        amountTotal: 1499,
        currency: 'USD',
      });
      return;
    }
    if (!sessionId) {
      setState({ phase: 'error', message: 'Missing session_id in URL.' });
      return;
    }

    const poll = async () => {
      if (finished.current) return;
      pollCount.current += 1;
      try {
        const res = await apiClient.request(`/api/billing/checkout/status/${sessionId}`);
        if (res.payment_status === 'paid') {
          finished.current = true;
          setState({
            phase: 'success',
            message: res.purpose === 'subscription'
              ? `Your ${res.plan_or_pack_id?.replace('_', ' ')} plan is now active.`
              : 'Your Hearts have been credited.',
            purpose: res.purpose,
            // Pass through Hearts-specific extras for the celebration card.
            heartsCredited: res.hearts_credited || 0,
            newBalance: res.new_balance || 0,
            parentEmailMasked: res.parent_email_masked,
            statementDescriptor: res.statement_descriptor || 'HIBFF TEEN COACHING',
            amountTotal: res.amount_total || 0,
            currency: res.currency || 'USD',
          });
          return;
        }
        if (res.status === 'expired') {
          finished.current = true;
          setState({ phase: 'error', message: 'Payment session expired. Please try again.' });
          return;
        }
        if (pollCount.current >= MAX_POLLS) {
          finished.current = true;
          setState({
            phase: 'pending',
            message: 'Payment is still processing. We\'ll email you the moment it clears. Your plan/Hearts will appear within a few minutes.',
          });
          return;
        }
        setState({ phase: 'checking', message: `Confirming your payment with Stripe…  (${pollCount.current}/${MAX_POLLS})` });
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (err) {
        finished.current = true;
        setState({ phase: 'error', message: err?.message || 'Could not confirm payment. Please contact support.' });
      }
    };

    poll();
    return () => { finished.current = true; };
    // Safe Addition: `searchParams` is the React Router hook object; its
    // identity flips when the URL query changes, which is precisely when
    // we DO want to re-poll (new session or preview mode). `sessionId` is
    // derived from it — listing both is the linter-preferred form.
  }, [sessionId, searchParams]);

  // Hearts purchases get a fully-branded celebration screen.
  if (state.phase === 'success' && state.purpose === 'hearts') {
    return (
      <DashboardLayout>
        <div className="py-10 px-4">
          <HeartsReceiptCelebration
            heartsCredited={state.heartsCredited}
            newBalance={state.newBalance}
            parentEmailMasked={state.parentEmailMasked}
            statementDescriptor={state.statementDescriptor}
            amountTotal={state.amountTotal}
            currency={state.currency}
            sessionId={sessionId}
          />
        </div>
      </DashboardLayout>
    );
  }

  const icon = state.phase === 'success'
    ? <CheckCircle2 size={56} className="text-emerald-500" />
    : state.phase === 'error'
    ? <XCircle size={56} className="text-red-500" />
    : state.phase === 'pending'
    ? <AlertCircle size={56} className="text-amber-500" />
    : <Loader2 size={56} className="text-purple-500 animate-spin" />;

  const heading = state.phase === 'success'
    ? 'Payment confirmed 🎉'
    : state.phase === 'error'
    ? 'Something went wrong'
    : state.phase === 'pending'
    ? 'Processing…'
    : 'Confirming payment…';

  return (
    <DashboardLayout>
      <div className="max-w-xl mx-auto py-16 px-4 text-center" data-testid="billing-success-root">
        <div className="inline-flex items-center justify-center mb-6">{icon}</div>
        <h1 className="text-3xl font-bold text-gray-900 mb-3" data-testid="billing-success-heading">{heading}</h1>
        <p className="text-gray-600 mb-8" data-testid="billing-success-message">{state.message}</p>
        <div className="flex items-center justify-center gap-3">
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 rounded-xl bg-gray-100 text-gray-700 font-semibold hover:bg-gray-200"
            data-testid="billing-success-home"
          >Back to dashboard</button>
          {state.phase === 'error' && (
            <button
              onClick={() => navigate('/pricing')}
              className="px-6 py-3 rounded-xl text-white font-semibold"
              style={{ backgroundColor: '#7B4FD8' }}
              data-testid="billing-success-retry"
            >Try again</button>
          )}
        </div>
        {sessionId && (
          <p className="text-[11px] text-gray-400 mt-8 font-mono break-all" data-testid="billing-success-session">{sessionId}</p>
        )}
      </div>
    </DashboardLayout>
  );
};

export default BillingSuccess;
